import React, { useEffect, useState } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { TopNavigation } from '@/components/TopNavigation'
import { ColorSquare } from '@/components/ColorSquare'
import { Toast } from '@/components/Toast'
import { useAuth } from '@/hooks/useAuth'
import { ClinicianService, UserProfileHelper } from '@lura/shared'
import type { ClinicianWithPatientCount } from '@lura/shared'

export const ClinicianListPage: React.FC = () => {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [clinicians, setClinicians] = useState<ClinicianWithPatientCount[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Study Admin access check
  if (!UserProfileHelper.isStudyAdmin(user)) {
    return <Navigate to="/studies" replace />
  }

  useEffect(() => {
    const fetchClinicians = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await ClinicianService.getAllClinicians()
        setClinicians(data)
      } catch (err) {
        console.error('Error fetching clinicians:', err)
        setError(
          err instanceof Error ? err.message : 'Failed to load clinicians'
        )
      } finally {
        setLoading(false)
      }
    }

    fetchClinicians()
  }, [])

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  return (
    <div className="min-h-screen bg-off-white">
      <TopNavigation
        currentPage="Clinicians"
        onSignOut={handleSignOut}
        user={user}
      />

      <main className="max-w-7xl mx-auto py-8 px-6">
        <div className="px-4 pb-6 sm:px-0">
          {/* Page Header */}
          <h2
            className="text-5xl font-normal text-dark-blue mb-8"
            style={{ fontFamily: 'Family' }}
          >
            Clinicians
          </h2>

          {/* Error State for page-level errors (loading data) */}
          {error && (
            <div className="mb-6 rounded-3xl bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          )}

          {/* Clinicians Table */}
          {!loading && !error && (
            <div className="bg-white shadow-sm rounded-3xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  All Clinicians
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  All clinicians in the system
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-highlight-green">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Clinician
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Studies
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Assigned Patients
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">View</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {clinicians.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-6 py-12 text-center text-sm text-gray-500"
                        >
                          No clinicians in the system
                        </td>
                      </tr>
                    ) : (
                      clinicians.map(clinician => (
                        <tr
                          key={clinician.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td
                            className="px-6 py-4 whitespace-nowrap cursor-pointer"
                            onClick={() =>
                              navigate(`/clinicians/${clinician.id}`)
                            }
                          >
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {clinician.first_name} {clinician.last_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {clinician.email}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              {clinician.studies &&
                                clinician.studies.length > 0 &&
                                clinician.studies
                                  .filter(
                                    (study: {
                                      id: string
                                      title: string
                                      color: string
                                    }) => study.id && study.title
                                  )
                                  .map(
                                    (study: {
                                      id: string
                                      title: string
                                      color: string
                                    }) => (
                                      <button
                                        key={study.id}
                                        onClick={e => {
                                          e.stopPropagation()
                                          navigate(`/studies/${study.id}`)
                                        }}
                                        className="flex items-center gap-2 hover:opacity-80 transition-opacity text-left w-full"
                                      >
                                        <ColorSquare
                                          color={study.color}
                                          size="sm"
                                        />
                                        <span className="text-sm text-gray-900">
                                          {study.title}
                                        </span>
                                      </button>
                                    )
                                  )}
                            </div>
                          </td>
                          <td
                            className="px-6 py-4 whitespace-nowrap cursor-pointer"
                            onClick={() =>
                              navigate(`/clinicians/${clinician.id}`)
                            }
                          >
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                              {clinician.patient_count}{' '}
                              {clinician.patient_count === 1
                                ? 'patient'
                                : 'patients'}
                            </span>
                          </td>
                          <td
                            className="px-6 py-4 whitespace-nowrap cursor-pointer"
                            onClick={() =>
                              navigate(`/clinicians/${clinician.id}`)
                            }
                          >
                            <div className="flex justify-end">
                              <svg
                                className="w-5 h-5 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Toast Notifications */}
      <Toast
        message={successMessage}
        type="success"
        onClose={() => setSuccessMessage(null)}
      />
      <Toast
        message={errorMessage}
        type="error"
        onClose={() => setErrorMessage(null)}
      />
    </div>
  )
}
